import OnboardingLayout from "component/auth/OnboardingLayout";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";

import FailHandler from "../../../component/failhandler";
import {
    fetchCurrentBusiness,
    handleUpdateBusiness,
} from "api/fetchers";
import {
    handleFetchBusinessById,
    handleUpdateBusinessDocuments,
    handleUploadOnboardingDocument,
} from "api/onboarding";
import { CheckCircleIcon } from "asset/icons";
import Loader from "component/loader";
import { trackOnboardingStep } from "utilities/googleAnalytics/trackOnboardingStep";
import UploadInputField from "component/auth/onboarding/UploadInputField";
import { getComplianceSampleDocuments } from "component/auth/onboarding/onboardingSampleDocuments";

const steps = [
    { title: "Business information" },
    { title: "Business verification", subtitles: ["Compliance", "Wallets"] },
    { title: "Business ownership" },
];

const LLCDocuments = ({ currentStep = 1, onNext, onBack }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const shouldReturnToSummary =
        new URLSearchParams(location.search).get("returnTo") === "summary";
    const [currentBusiness, setCurrentBusiness] = useState(null);
    const [fails, setFails] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [selectedCACOption, setSelectedCACOption] = useState("CAC_STATUS_REPORT");
    const [uploadMode, setUploadMode] = useState("single");
    const [loader, setLoader] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState({});
    const [uploadProgress, setUploadProgress] = useState({});
    const [bankStatementPrepopulated, setBankStatementPrepopulated] = useState(false);
    const [showRequiredErrors, setShowRequiredErrors] = useState(false);

    const documentKeyMap = {
        CERTIFICATE_OF_INCORPORATION: "CAC",
        TRADE_BUSINESS_LICENSE: "BUSINESS_LICENSE",
        OPERATING_AGREEMENT: "ARTICLE_OF_INCORPORATION",
        BANK_STATEMENT: "BankStatement",
    };

    // File upload states
    const [fileUploaded, setFileUploaded] = useState({
        CAC: false,
        CERTIFICATE_OF_INCORPORATION: false,
        COMMERCIAL_REGISTRATION_CERTIFICATE: false,
        TRADE_BUSINESS_LICENSE: false,
        MEMORANDUM_AND_ARTICLE_OF_ASSOCIATION: false,
        OPERATING_AGREEMENT: false,
        BOARD_RESOLUTION: false,
        BANK_STATEMENT: false,
        bvn: false,
        CAC_STATUS_REPORT: false,
        CAC_FORMS: false,
        CAC_FORM_2: false,
        CAC_FORM_2_1: false,
        CAC_FORM_3: false,
        CAC_FORM_4: false,
        CAC_FORM_7: false,
    });

    const [loading, setLoading] = useState({
        CAC: false,
        CERTIFICATE_OF_INCORPORATION: false,
        COMMERCIAL_REGISTRATION_CERTIFICATE: false,
        TRADE_BUSINESS_LICENSE: false,
        MEMORANDUM_AND_ARTICLE_OF_ASSOCIATION: false,
        OPERATING_AGREEMENT: false,
        BOARD_RESOLUTION: false,
        BANK_STATEMENT: false,
        bvn: false,
        CAC_STATUS_REPORT: false,
        CAC_FORM_1: false,
        CAC_FORM_2: false,
        CAC_FORM_2_1: false,
        CAC_FORM_3: false,
        CAC_FORM_4: false,
        CAC_FORM_7: false,
        COMBINED_CAC_FORM: false,
    });

    useEffect(() => {
        const getBusinessData = async () => {
            try {
                const data = await fetchCurrentBusiness();
                const currentBusinessData = data?.business || data;
                let business = currentBusinessData;

                if (currentBusinessData?._id) {
                    const businessDetails = await handleFetchBusinessById(currentBusinessData._id);
                    business = businessDetails?.business || businessDetails || currentBusinessData;
                }

                setCurrentBusiness(business);

                const existingDocuments = business?.documents || {};
                const apiToFormKey = {
                    BankStatement: "BANK_STATEMENT",
                    BANK_STATEMENT: "BANK_STATEMENT",
                    BUSINESS_LICENSE: "TRADE_BUSINESS_LICENSE",
                    ARTICLE_OF_INCORPORATION: "OPERATING_AGREEMENT",
                };
                const hydratedFiles = {};
                const hydratedFlags = {};
                Object.entries(existingDocuments).forEach(([apiKey, document]) => {
                    const formKey = apiToFormKey[apiKey] || apiKey;
                    const url = getUploadUrl(document);
                    const key = getUploadKey(document, apiKey);
                    if (!url && !key) return;
                    hydratedFiles[formKey] = {
                        ...((document && typeof document === "object") ? document : {}),
                        name: document?.name || document?.key || apiKey,
                        size: document?.size || 0,
                        ...(url ? { url } : {}),
                        ...(key ? { key } : {}),
                    };
                    hydratedFlags[formKey] = true;
                });
                setUploadedFiles((prev) => ({ ...prev, ...hydratedFiles }));
                setFileUploaded((prev) => ({ ...prev, ...hydratedFlags }));

                if (hydratedFlags.COMBINED_CAC_FORM) {
                    setSelectedCACOption("CAC_FORMS");
                    setUploadMode("single");
                } else if (["CAC_FORM_2", "CAC_FORM_2_1", "CAC_FORM_3", "CAC_FORM_4", "CAC_FORM_7"].some((key) => hydratedFlags[key])) {
                    setSelectedCACOption("CAC_FORMS");
                    setUploadMode("multiple");
                } else if (hydratedFlags.CAC_STATUS_REPORT) {
                    setSelectedCACOption("CAC_STATUS_REPORT");
                }

                const bankStatementProof = getBankStatementProofFile(business);
                if (bankStatementProof) {
                    setBankStatementPrepopulated(true);
                    setFileUploaded((prev) => ({ ...prev, BANK_STATEMENT: true }));
                    setUploadedFiles((prev) => ({
                        ...prev,
                        BANK_STATEMENT: bankStatementProof,
                    }));
                }
            } catch (error) {
                console.error("Failed to fetch current business:", error);
            }
        };
        getBusinessData();
    }, []);

    // Clear error message after 3 seconds if an error occurs
    useEffect(() => {
        if (fails) {
            const timer = setTimeout(() => {
                setFails(false);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [fails]);

    const business = currentBusiness?.business || currentBusiness;
    const businessCountryName = business?.country || business?.businessCountry?.name;
    const businessCountryCode = business?.businessCountry?.code;
    const businessContinent = business?.businessCountry?.continent;
    const middleEastContinents = ["ME", "MENA", "Middle East", "Middle-East"];
    const middleEastCountryCodes = [
        "AE",
        "BH",
        "CY",
        "EG",
        "IL",
        "IQ",
        "IR",
        "JO",
        "KW",
        "LB",
        "OM",
        "PS",
        "QA",
        "SA",
        "SY",
        "TR",
        "YE",
    ];
    const middleEastCountryNames = [
        "United Arab Emirates",
        "Bahrain",
        "Cyprus",
        "Egypt",
        "Israel",
        "Iraq",
        "Iran",
        "Jordan",
        "Kuwait",
        "Lebanon",
        "Oman",
        "Palestine",
        "Qatar",
        "Saudi Arabia",
        "Syria",
        "Turkey",
        "Yemen",
    ];
    const isNigeria = businessCountryName === "Nigeria" || businessCountryCode === "NG";
    const isMiddleEast =
        middleEastContinents.includes(businessContinent) ||
        middleEastCountryCodes.includes(businessCountryCode) ||
        middleEastCountryNames.includes(businessCountryName);
    const sampleDocuments = getComplianceSampleDocuments(
        businessCountryName || businessCountryCode
    );

    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB limit

    const validateFile = (file) => {
        if (!file) return false;
        if (file.size > MAX_FILE_SIZE) {
            setFails(true);
            setErrorMessage(`File size exceeds the limit of 5MB.`);
            return false;
        }
        return true;
    };

    const getUploadUrl = (raw) =>
        (typeof raw === "string" && raw) ||
        raw?.url ||
        raw?.url?.link ||
        raw?.link ||
        raw?.data?.url ||
        raw?.data?.url?.link ||
        raw?.data?.link ||
        "";

    const getUploadKey = (raw, fallback = "") =>
        raw?.key ||
        raw?.fileKey ||
        raw?.name ||
        raw?.Key ||
        raw?.data?.key ||
        raw?.data?.fileKey ||
        raw?.data?.name ||
        raw?.data?.Key ||
        raw?.path ||
        raw?.data?.path ||
        fallback;

    const getApiDocumentKey = (fileKey) => documentKeyMap[fileKey] || fileKey;

    const handleFileUpload = async (event, fileKey) => {
        const file = event.target.files[0];
        if (!validateFile(file)) return;

        setLoading((prev) => ({ ...prev, [fileKey]: true }));
        setUploadedFiles((prev) => ({ ...prev, [fileKey]: file }));
        setUploadProgress((prev) => ({ ...prev, [fileKey]: 0 }));

        // Simulate progress
        let progress = 0;
        const interval = setInterval(() => {
            progress += 10;
            setUploadProgress((prev) => ({ ...prev, [fileKey]: progress }));
            if (progress >= 100) clearInterval(interval);
        }, 100);

        try {
            const response = await handleUploadOnboardingDocument(file);
            const uploadData = response?.data || response;
            const url = getUploadUrl(uploadData);

            if (!url) {
                throw new Error(response?.message || "No upload URL returned");
            }

            setUploadedFiles((prev) => ({
                ...prev,
                [fileKey]: {
                    ...file,
                    name: file.name,
                    size: file.size,
                    url,
                    key: getUploadKey(uploadData, file.name),
                },
            }));
            setFileUploaded((prev) => ({ ...prev, [fileKey]: true }));
        } catch (error) {
            setFails(true);
            setErrorMessage(error?.message || "An error occurred while uploading the file");
            setFileUploaded((prev) => ({ ...prev, [fileKey]: false }));
            setUploadProgress((prev) => ({ ...prev, [fileKey]: 0 }));
        } finally {
            setLoading((prev) => ({ ...prev, [fileKey]: false }));
        }
    };

    const handleRemoveFile = (fileKey) => {
        if (fileKey === "BANK_STATEMENT" && bankStatementPrepopulated) return;

        setUploadedFiles((prev) => ({ ...prev, [fileKey]: null }));
        setFileUploaded((prev) => ({ ...prev, [fileKey]: false }));
        setUploadProgress((prev) => ({ ...prev, [fileKey]: 0 }));
    };

    function getBankStatementProofFile(businessData) {
        const proof = businessData?.proofOfAddress;
        const isBankStatement = [proof?.type, proof?.key, proof?.name, proof?.url, proof?.link].some(
            (value) => typeof value === "string" && value.toLowerCase().includes("bank")
        );

        const existingBankStatement =
            businessData?.documents?.BankStatement ||
            businessData?.documents?.BANK_STATEMENT ||
            businessData?.documents?.bankStatement;

        if ((!proof || !isBankStatement) && !existingBankStatement) return null;

        const source = isBankStatement ? proof : existingBankStatement;
        const sourceFields = typeof source === "object" && source !== null ? source : {};

        const url = getUploadUrl(source);
        const key = getUploadKey(source, source.name || source.type || "Bank statement");

        return {
            ...sourceFields,
            name: source.name || source.key || source.type || "Bank statement",
            size: source.size || 0,
            ...(url ? { url } : {}),
            ...(key ? { key } : {}),
        };
    }

    const allFilesUploaded = () => {
        if (!business?.type || !businessCountryName) return false;

        const type = business.type;

        if (type !== "LLC") return false;

        if (isMiddleEast) {
            return (
                fileUploaded["COMMERCIAL_REGISTRATION_CERTIFICATE"] &&
                fileUploaded["TRADE_BUSINESS_LICENSE"] &&
                fileUploaded["OPERATING_AGREEMENT"] &&
                fileUploaded["BOARD_RESOLUTION"] &&
                fileUploaded["BANK_STATEMENT"]
            );
        }

        if (!isNigeria) {
            return (
                fileUploaded["CERTIFICATE_OF_INCORPORATION"] &&
                fileUploaded["OPERATING_AGREEMENT"] &&
                fileUploaded["BOARD_RESOLUTION"] &&
                fileUploaded["BANK_STATEMENT"]
            );
        }

        const nigeriaRequired =
            fileUploaded["CAC"] &&
            fileUploaded["MEMORANDUM_AND_ARTICLE_OF_ASSOCIATION"] &&
            fileUploaded["BOARD_RESOLUTION"] &&
            fileUploaded["BANK_STATEMENT"];

        if (!nigeriaRequired) return false;

        if (selectedCACOption === "CAC_STATUS_REPORT") {
            return fileUploaded["CAC_STATUS_REPORT"];
        }

        if (selectedCACOption === "CAC_FORMS") {
            if (uploadMode === "single") {
                return fileUploaded["COMBINED_CAC_FORM"];
            } else {
                return (
                    fileUploaded["CAC_FORM_2"] &&
                    fileUploaded["CAC_FORM_2_1"] &&
                    fileUploaded["CAC_FORM_3"] &&
                    fileUploaded["CAC_FORM_4"] &&
                    fileUploaded["CAC_FORM_7"]
                );
            }
        }

        return false;
    };

    const getRequiredDocumentKeys = () => {
        if (isMiddleEast) {
            return [
                "COMMERCIAL_REGISTRATION_CERTIFICATE",
                "TRADE_BUSINESS_LICENSE",
                "OPERATING_AGREEMENT",
                "BOARD_RESOLUTION",
                "BANK_STATEMENT",
            ];
        }

        if (!isNigeria) {
            return [
                "CERTIFICATE_OF_INCORPORATION",
                "OPERATING_AGREEMENT",
                "BOARD_RESOLUTION",
                "BANK_STATEMENT",
            ];
        }

        const baseKeys = [
            "CAC",
            "MEMORANDUM_AND_ARTICLE_OF_ASSOCIATION",
            "BOARD_RESOLUTION",
            "BANK_STATEMENT",
        ];

        if (selectedCACOption === "CAC_STATUS_REPORT") {
            return [...baseKeys, "CAC_STATUS_REPORT"];
        }

        if (selectedCACOption === "CAC_FORMS") {
            if (uploadMode === "single") {
                return [...baseKeys, "COMBINED_CAC_FORM"];
            }

            return [
                ...baseKeys,
                "CAC_FORM_2",
                "CAC_FORM_2_1",
                "CAC_FORM_3",
                "CAC_FORM_4",
                "CAC_FORM_7",
            ];
        }

        return baseKeys;
    };

    const buildDocumentsPayload = () => {
        const documents = {};

        getRequiredDocumentKeys().forEach((fileKey) => {
            if (fileKey === "BANK_STATEMENT" && bankStatementPrepopulated) return;

            const file = uploadedFiles[fileKey];
            const url = getUploadUrl(file);
            const key = getUploadKey(file, file?.name || "");

            if (!url && !key) return;

            documents[getApiDocumentKey(fileKey)] = {
                ...(url ? { url } : {}),
                ...(key ? { key } : {}),
            };
        });

        return { documents };
    };

    const isRequiredDocumentMissing = (fileKey) =>
        getRequiredDocumentKeys().includes(fileKey) &&
        !(fileUploaded[fileKey] ||
            (fileKey === "BANK_STATEMENT" && bankStatementPrepopulated));

    const handleContinue = async () => {
        if (allFilesUploaded()) {
            setLoader(true);
            try {
                await handleUpdateBusinessDocuments(business?._id, buildDocumentsPayload());
                await handleUpdateBusiness(business?._id);

                if (localStorage.getItem("default") !== null) {
                    localStorage.removeItem("default");
                }
                localStorage.setItem("hide", false);
                trackOnboardingStep("onboarding_documents_uploaded", business._id);

                trackOnboardingStep("onboarding_completed", business._id);

                navigate(
                    shouldReturnToSummary
                        ? "/onboarding/summary"
                        : "/onboarding/business-verification/wallets"
                );
            } catch (error) {
                setFails(true);
                setErrorMessage(error?.message || "An error occurred during verification.");
            } finally {
                setLoader(false);
            }
        } else {
            setShowRequiredErrors(true);
            setFails(true);
            setErrorMessage(
                isNigeria
                    ? "Please ensure you have uploaded: Certificate of Incorporation, Memorandum & Articles of Association, Board Resolution, Bank Statement, and the selected CAC document(s)."
                    : "Please ensure you have uploaded all required business verification documents."
            );
            requestAnimationFrame(() => {
                const firstMissingKey = getRequiredDocumentKeys().find(isRequiredDocumentMissing);
                const field = document.querySelector(
                    `[data-onboarding-field="${firstMissingKey}"]`
                );
                field?.scrollIntoView({ behavior: "smooth", block: "center" });
                field?.querySelector("input")?.focus({ preventScroll: true });
            });
        }
    };

    const renderUploadField = ({ key, label, subtitle, sample }) => (
        <div key={key} data-onboarding-field={key}>
            <UploadInputField
                label={label}
                subtitle={subtitle}
                fileKey={key}
                seeSampleDoc={sample || sampleDocuments[label]}
                fileState={{
                    file: uploadedFiles[key],
                    loading: loading[key],
                    uploaded: fileUploaded[key],
                    progress: uploadProgress[key] || 0,
                }}
                onFileChange={(e) => handleFileUpload(e, key)}
                onRemove={() => handleRemoveFile(key)}
                disabled={key === "BANK_STATEMENT" && bankStatementPrepopulated}
                validationError={
                    showRequiredErrors && isRequiredDocumentMissing(key)
                        ? "This field is required."
                        : ""
                }
            />
        </div>
    );

    const llcDocumentFields = (() => {
        if (isMiddleEast) {
            return [
                {
                    key: "COMMERCIAL_REGISTRATION_CERTIFICATE",
                    label: "Commercial Registration Certificate",
                },
                {
                    key: "TRADE_BUSINESS_LICENSE",
                    label: "Trade/Business License",
                },
                {
                    key: "OPERATING_AGREEMENT",
                    label: "Operating Agreement or Articles of Incorporation",
                },
                {
                    key: "BOARD_RESOLUTION",
                    label: "Board resolution to open an account with Tradevu",
                    sample: sampleDocuments["Board resolution to open an account"],
                },
                {
                    key: "BANK_STATEMENT",
                    label: "Bank statement or letter (last 6 months)",
                },
            ];
        }

        if (isNigeria) {
            return [
                {
                    key: "CAC",
                    label: "Certificate of Incorporation or Business License",
                    subtitle: "From your country",
                },
                {
                    key: "MEMORANDUM_AND_ARTICLE_OF_ASSOCIATION",
                    label: "Memorandum & Articles of Association",
                },
                {
                    key: "BOARD_RESOLUTION",
                    label: "Board resolution to open an account",
                },
                {
                    key: "BANK_STATEMENT",
                    label: "Bank statement (last 6 months)",
                },
            ];
        }

        return [
            {
                key: "CERTIFICATE_OF_INCORPORATION",
                label: "Certificate of Incorporation or Business License",
            },
            {
                key: "OPERATING_AGREEMENT",
                label: "Operating Agreement or Articles of Incorporation",
            },
            {
                key: "BOARD_RESOLUTION",
                label: "Board resolution to open an account with Tradevu",
                sample: sampleDocuments["Board resolution to open an account"],
            },
            {
                key: "BANK_STATEMENT",
                label: "Bank statement (last 6 months)",
            },
        ];
    })();

    return (
        <OnboardingLayout
            title="Activate account"
            steps={steps}
            currentStep={currentStep}
            currentSubStep={0}
        >
            <div className=" max-w-[1100px] md:pl-96 ">
                <Helmet>
                    <title>Verify business</title>
                </Helmet>

                <div className="">
                    <FailHandler success={fails} message={errorMessage} />
                    <h2 className="aku-heading-h2-medium text-[#101828]">
                        Verify business
                    </h2>

                    <div className="mt-8 space-y-4">
                        {llcDocumentFields.map(renderUploadField)}
                    </div>

                    {isNigeria && (
                        <div className="mt-6">
                            <div>
                                <p className="aku-body-small-medium text-[#4F5372] !mb-1">
                                    Upload either your{" "}
                                    <span className="font-medium">Status Report</span> or{" "}
                                    <span className="font-medium">Forms 2-7</span>
                                </p>

                                <div className="flex items-center gap-6">
                                    {/* Status Report Option */}
                                    <div className="flex items-center">
                                        <input
                                            type="radio"
                                            id="status-report-radio"
                                            name="cacOption"
                                            value="CAC_STATUS_REPORT"
                                            className="hidden"
                                            checked={selectedCACOption === "CAC_STATUS_REPORT"}
                                            onChange={() =>
                                                setSelectedCACOption("CAC_STATUS_REPORT")
                                            }
                                        />
                                        <label
                                            htmlFor="status-report-radio"
                                            className="flex items-center justify-center cursor-pointer"
                                        >
                                            {selectedCACOption === "CAC_STATUS_REPORT" ? (
                                                <CheckCircleIcon className="size-5" />
                                            ) : (
                                                <CheckCircleIcon className="size-5 *:fill-[#DBDFE5]" />
                                            )}
                                        </label>
                                        <span
                                            className="aku-body-small-medium ml-2 text-[#4F5372] cursor-pointer"
                                            onClick={() =>
                                                setSelectedCACOption("CAC_STATUS_REPORT")
                                            }
                                        >
                                            Status Report
                                        </span>
                                    </div>

                                    {/* CAC Forms Option */}
                                    <div className="flex items-center">
                                        <input
                                            type="radio"
                                            id="cac-forms-radio"
                                            name="cacOption"
                                            value="CAC_FORMS"
                                            className="hidden"
                                            checked={selectedCACOption === "CAC_FORMS"}
                                            onChange={() => setSelectedCACOption("CAC_FORMS")}
                                        />
                                        <label
                                            htmlFor="cac-forms-radio"
                                            className="flex items-center justify-center cursor-pointer"
                                        >
                                            {selectedCACOption === "CAC_FORMS" ? (
                                                <CheckCircleIcon className="size-5" />
                                            ) : (
                                                <CheckCircleIcon className="size-5 *:fill-[#DBDFE5]" />
                                            )}
                                        </label>
                                        <span
                                            className="aku-body-small-medium ml-2 text-[#4F5372] cursor-pointer"
                                            onClick={() => setSelectedCACOption("CAC_FORMS")}
                                        >
                                            CAC Forms (2, 2.1, 3, 4, and 7)
                                        </span>
                                    </div>
                                </div>

                                {selectedCACOption === "CAC_STATUS_REPORT" && (
                                    <div
                                        className="mt-4 rounded-lg"
                                        data-onboarding-field="CAC_STATUS_REPORT"
                                    >
                                        <UploadInputField
                                            label="CAC Status Report"
                                            subtitle="JPEG, PNG, PDF ・ 5 MB max."
                                            seeSampleDoc={sampleDocuments["CAC Status Report"]}
                                            fileKey="CAC_STATUS_REPORT"
                                            fileState={{
                                                file: uploadedFiles["CAC_STATUS_REPORT"],
                                                loading: loading["CAC_STATUS_REPORT"],
                                                uploaded: fileUploaded["CAC_STATUS_REPORT"],
                                                progress: uploadProgress["CAC_STATUS_REPORT"] || 0,
                                            }}
                                            onFileChange={(e) =>
                                                handleFileUpload(e, "CAC_STATUS_REPORT")
                                            }
                                            onRemove={() => handleRemoveFile("CAC_STATUS_REPORT")}
                                            validationError={
                                                showRequiredErrors &&
                                                isRequiredDocumentMissing("CAC_STATUS_REPORT")
                                                    ? "This field is required."
                                                    : ""
                                            }
                                        />
                                    </div>
                                )}

                                {/* Toggle for Single Upload / Multiple Uploads when CAC Forms is selected */}
                                {selectedCACOption === "CAC_FORMS" && (
                                    <div className="flex items-center mt-4 bg-[#F6F8FA] rounded-full w-max">
                                        <button
                                            className={`px-4 py-2 text-sm rounded-l-full ${
                                                uploadMode === "single"
                                                    ? "bg-[#6F00FF] text-white"
                                                    : "bg-transparent text-[#6F739B] border"
                                            }`}
                                            onClick={() => setUploadMode("single")}
                                        >
                                            Single Upload
                                        </button>
                                        <button
                                            className={`px-4 py-2 text-sm rounded-r-full ${
                                                uploadMode === "multiple"
                                                    ? "bg-[#6F00FF] text-white"
                                                    : "bg-transparent text-[#6F739B] border"
                                            }`}
                                            onClick={() => setUploadMode("multiple")}
                                        >
                                            Multiple Uploads
                                        </button>
                                    </div>
                                )}

                                {/* Single Upload Section */}
                                {selectedCACOption === "CAC_FORMS" && uploadMode === "single" && (
                                    <div
                                        className="mt-4 bg-[#F6F8FA] rounded-lg"
                                        data-onboarding-field="COMBINED_CAC_FORM"
                                    >
                                        <UploadInputField
                                            label="CAC Forms 2, 2.1, 3, 4, and 7"
                                            subtitle="PDF or DOCX ・ 5 MB max."
                                            seeSampleDoc={
                                                sampleDocuments[
                                                    "CAC Forms (2, 2.1, 3, 4, and 7)"
                                                ]
                                            }
                                            fileKey="COMBINED_CAC_FORM"
                                            fileState={{
                                                file: uploadedFiles["COMBINED_CAC_FORM"],
                                                loading: loading["COMBINED_CAC_FORM"],
                                                uploaded: fileUploaded["COMBINED_CAC_FORM"],
                                                progress: uploadProgress["COMBINED_CAC_FORM"] || 0,
                                            }}
                                            onFileChange={(e) =>
                                                handleFileUpload(e, "COMBINED_CAC_FORM")
                                            }
                                            onRemove={() => handleRemoveFile("COMBINED_CAC_FORM")}
                                            validationError={
                                                showRequiredErrors &&
                                                isRequiredDocumentMissing("COMBINED_CAC_FORM")
                                                    ? "This field is required."
                                                    : ""
                                            }
                                        />
                                    </div>
                                )}

                                {/* Multiple Uploads Section */}
                                {selectedCACOption === "CAC_FORMS" &&
                                    uploadMode === "multiple" &&
                                    [
                                        {
                                            key: "CAC_FORM_2",
                                            label: "CAC Form 2",
                                            description:
                                                "Statement of share capital and return of allotment of shares",
                                        },
                                        {
                                            key: "CAC_FORM_2_1",
                                            label: "CAC Form 2.1",
                                            description: "Particulars of secretary",
                                        },
                                        {
                                            key: "CAC_FORM_3",
                                            label: "CAC Form 3",
                                            description: "Notice of registered addresses",
                                        },
                                        {
                                            key: "CAC_FORM_4",
                                            label: "CAC Form 4",
                                            description: "Declaration of compliance",
                                        },
                                        {
                                            key: "CAC_FORM_7",
                                            label: "CAC Form 7",
                                            description: "Particulars of directors",
                                        },
                                    ].map(({ key, label, description }) => (
                                        <div
                                            key={key}
                                            className="!mt-6"
                                            data-onboarding-field={key}
                                        >
                                            <p className="text-sm font-semibold text-[#101828]">
                                                {label}
                                                <span className="text-[#6F739B] font-normal ml-2">
                                                    ・ {description}
                                                </span>
                                            </p>
                                            <div className="">
                                                <UploadInputField
                                                    label={label}
                                                    hideLabel
                                                    subtitle="JPEG, PNG, PDF ・ 5 MB max."
                                                    seeSampleDoc={sampleDocuments[label]}
                                                    fileKey={key}
                                                    fileState={{
                                                        file: uploadedFiles[key],
                                                        loading: loading[key],
                                                        uploaded: fileUploaded[key],
                                                        progress: uploadProgress[key] || 0,
                                                    }}
                                                    onFileChange={(e) => handleFileUpload(e, key)}
                                                    onRemove={() => handleRemoveFile(key)}
                                                    validationError={
                                                        showRequiredErrors &&
                                                        isRequiredDocumentMissing(key)
                                                            ? "This field is required."
                                                            : ""
                                                    }
                                                />
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    )}
                    <div className="flex justify-end">
                        <button
                            className={`mt-4 text-base font-normal rounded-lg  w-full md:w-auto h-12 px-6 flex items-center justify-center
            ${
                allFilesUploaded()
                    ? "bg-[#6F00FF] text-white"
                    : "bg-[#E9D7FE] text-white cursor-not-allowed"
            }`}
                            onClick={handleContinue}
                            disabled={loader}
                            aria-disabled={!allFilesUploaded() || loader}
                        >
                            {loader ? <Loader /> : "Save and Continue"}
                        </button>
                    </div>
                </div>
            </div>
        </OnboardingLayout>
    );
};

export default LLCDocuments;
